import { useCallback, useEffect, useRef, useState } from "react";
import { zim } from "../../../../zegoConfig";
import { loginToZego } from "../../../../components/utils/zegoLogin";
import { TeleconsultationData } from "../types";

const JOIN_GROUP_MAX_ATTEMPTS = 5;
const JOIN_GROUP_RETRY_DELAY_MS = 2000;
const JOIN_GROUP_SLOW_RETRY_DELAY_MS = 15000;
const LOGIN_RETRY_DELAY_MS = 3000;
const LOGIN_MAX_RETRY_DELAY_MS = 15000;

// ZIM.ConnectionState.Disconnected
const ZIM_CONNECTION_STATE_DISCONNECTED = 0;
// ZIM.ConnectionEvent: LoginTimeout, LoginInterrupted, TokenExpired
const ZIM_RELOGIN_EVENTS = [2, 3, 5];

const createWaiter = () => {
  let wakeUp: (() => void) | null = null;

  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, ms);
      wakeUp = () => {
        clearTimeout(timer);
        resolve();
      };
    });

  return { wait, wake: () => wakeUp?.() };
};

interface UseZegoChatReturn {
  isLoggedIn: boolean;
  isGroupReady: boolean;
  userId: string;
  userDokter: string;
  groupId: string | null;
}

/**
 * Manages ZIM authentication and group joining.
 *
 * IMPORTANT: We intentionally do NOT call leaveGroup on unmount.
 * The user is added to the group from the backend when the consultation is created.
 * Leaving the group would cause missed messages when the user navigates away and returns.
 * The user should remain a group member for the full duration of the consultation.
 */
export function useZegoChat(
  teleconsultationData: TeleconsultationData | null,
): UseZegoChatReturn {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isGroupReady, setIsGroupReady] = useState(false);
  const [loginAttempt, setLoginAttempt] = useState(0);

  const hasJoinedGroupRef = useRef(false);
  const connectionListenerRegisteredRef = useRef(false);
  const isMountedRef = useRef(true);

  // ─── Derived identifiers ──────────────────────────────────────────────────

  const appEnv = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || "";

  const userId = teleconsultationData?.patient_id
    ? `LMS${teleconsultationData.patient_id}${appEnv}`
    : "";

  const username = teleconsultationData?.patient_name || "defaultUser";

  const userDokter = teleconsultationData?.practitioner_id
    ? `LMS${teleconsultationData.practitioner_id}${appEnv}`
    : "";

  const groupId = teleconsultationData?.room_id
    ? String(teleconsultationData.room_id).trim()
    : null;

  // ─── Join group ───────────────────────────────────────────────────────────

  const joinGroup = useCallback(
    async (gId: string): Promise<boolean> => {
      if (!gId) {
        console.error("❌ groupId kosong");
        return false;
      }

      console.log("👥 Mencoba joinGroup:", gId);

      try {
        await zim.joinGroup(gId);
        console.log("✅ Berhasil joinGroup:", gId);
        return true;
      } catch (error: any) {
        console.warn("⚠️ joinGroup error detail:", JSON.stringify(error));

        const errorCode =
          error?.code ?? error?.errorCode ?? error?.error?.code ?? 0;
        const errorMessage = error?.message ?? error?.error?.message ?? "";

        // Sudah member → anggap sukses, ini kondisi normal
        if (
          errorCode === 6000522 ||
          errorCode === 108037 ||
          errorMessage?.includes("already belong")
        ) {
          console.log("✅ Sudah member group, lanjut:", gId);
          return true;
        }

        // Group belum ada → buat baru
        if (errorCode === 660500002 || errorCode === 6000404) {
          try {
            console.log("🔄 Group tidak ada, mencoba createGroup:", gId);
            await zim.createGroup({ groupID: gId, groupName: gId }, [
              userDokter,
            ]);
            console.log("✅ Berhasil createGroup:", gId);
            return true;
          } catch (createError: any) {
            const createCode = createError?.code ?? createError?.errorCode ?? 0;
            const createMsg = createError?.message ?? "";
            if (
              createCode === 660500001 ||
              createMsg?.includes("already belong")
            ) {
              console.log("✅ Group sudah ada (race condition), lanjut:", gId);
              return true;
            }
            console.error("❌ Gagal createGroup:", createCode, createMsg);
            return false;
          }
        }

        console.error(
          "❌ Gagal joinGroup (unhandled):",
          errorCode,
          errorMessage,
        );
        return false;
      }
    },
    [userDokter],
  );

  // ─── Login ZIM ────────────────────────────────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId || !username) return;

    let cancelled = false;
    const { wait, wake } = createWaiter();
    window.addEventListener("online", wake);

    const loginWithRetry = async () => {
      for (let attempt = 1; !cancelled; attempt++) {
        console.log("🔑 Login ZIM untuk userId:", userId);
        const success = await loginToZego(userId, username);
        if (cancelled) return;
        if (success) {
          setIsLoggedIn(true);
          return;
        }
        console.warn(`⚠️ Login ZIM gagal (percobaan ${attempt})`);
        await wait(
          Math.min(LOGIN_RETRY_DELAY_MS * attempt, LOGIN_MAX_RETRY_DELAY_MS),
        );
      }
    };

    loginWithRetry();
    return () => {
      cancelled = true;
      wake();
      window.removeEventListener("online", wake);
    };
  }, [userId, username, loginAttempt]);

  useEffect(() => {
    if (connectionListenerRegisteredRef.current) return;
    connectionListenerRegisteredRef.current = true;

    zim.on(
      "connectionStateChanged",
      (_zim: any, data: { state: number; event: number }) => {
        if (!isMountedRef.current) return;
        if (
          data.state === ZIM_CONNECTION_STATE_DISCONNECTED &&
          ZIM_RELOGIN_EVENTS.includes(data.event)
        ) {
          console.warn("⚠️ ZIM terputus, login ulang. Event:", data.event);
          setIsLoggedIn(false);
          setLoginAttempt((prev) => prev + 1);
        }
      },
    );
  }, []);

  // ─── Join group setelah login ─────────────────────────────────────────────

  // Pesan group hanya dikirim realtime ke member, jadi join diulang sampai
  // berhasil. isGroupReady menandai kapan history ZIM aman di-query.
  useEffect(() => {
    if (!isLoggedIn || !groupId || hasJoinedGroupRef.current) return;
    hasJoinedGroupRef.current = true;

    let cancelled = false;
    const { wait, wake } = createWaiter();
    window.addEventListener("online", wake);

    const joinWithRetry = async () => {
      for (let attempt = 1; !cancelled; attempt++) {
        const joined = await joinGroup(groupId);
        if (cancelled) return;
        if (joined) {
          setIsGroupReady(true);
          return;
        }
        console.warn(`⚠️ joinGroup gagal (percobaan ${attempt})`);
        await wait(
          attempt < JOIN_GROUP_MAX_ATTEMPTS
            ? JOIN_GROUP_RETRY_DELAY_MS * attempt
            : JOIN_GROUP_SLOW_RETRY_DELAY_MS,
        );
      }
    };

    joinWithRetry();
    return () => {
      cancelled = true;
      wake();
      window.removeEventListener("online", wake);
      hasJoinedGroupRef.current = false;
    };
  }, [isLoggedIn, groupId, joinGroup]);

  // ─── TIDAK ada leaveGroup saat unmount ────────────────────────────────────
  // User tetap sebagai member group selama sesi konsultasi berlangsung.
  // Jika di-leave, pesan dokter tidak akan masuk saat user di luar halaman chat.
  // Backend yang menghandle penambahan & penghapusan member group.

  return { isLoggedIn, isGroupReady, userId, userDokter, groupId };
}
